import { useState, useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DollarSign, TrendingUp, MapPin, Building2, Wheat, Loader2 } from "lucide-react";
import "leaflet/dist/leaflet.css";

const FINANCING_OPPORTUNITIES = [
  { id: 1, name: "Cocoa Harvest Loan", amount: 25.8, change: 9.08, region: "West Africa", lat: 7.5, lng: -1.5, type: "Harvest", commodity: "Cocoa" },
  { id: 2, name: "Sugar Export Credit", amount: 31.4, change: 6.32, region: "East Africa", lat: -6.2, lng: 35.7, type: "Export", commodity: "Sugar" },
  { id: 3, name: "Wheat Supply Deal", amount: 28.9, change: 1.41, region: "North Africa", lat: 31.0, lng: 31.2, type: "Supply", commodity: "Wheat" },
  { id: 4, name: "Petroleum Shipment", amount: 65.2, change: 3.45, region: "Middle East", lat: 25.0, lng: 51.5, type: "Trade", commodity: "Crude Oil" },
  { id: 5, name: "Coffee Trade Finance", amount: 18.5, change: 4.22, region: "East Africa", lat: -1.3, lng: 36.8, type: "Trade", commodity: "Coffee" },
  { id: 6, name: "Natural Gas Import", amount: 55.1, change: 2.15, region: "Europe", lat: 52.5, lng: 13.4, type: "Import", commodity: "Natural Gas" },
  { id: 7, name: "Copper Mining Finance", amount: 42.3, change: 5.67, region: "Southern Africa", lat: -15.4, lng: 28.3, type: "Mining", commodity: "Copper" },
  { id: 8, name: "Maize Storage Facility", amount: 12.8, change: 2.89, region: "East Africa", lat: -4.0, lng: 39.7, type: "Storage", commodity: "Maize" },
];

const RECENTLY_FINANCED = [
  { name: "Hesse Nut Supplier", amount: 8.9, status: "Completed" },
  { name: "Maize Carrier", amount: 5.2, status: "In Progress" },
  { name: "Water Transfer", amount: 12.4, status: "Completed" },
  { name: "Sunflower Stock", amount: 6.8, status: "Pending" },
];

function FitBounds({ opportunities }) {
  const map = useMap();
  useEffect(() => {
    if (opportunities.length > 0) {
      const bounds = L.latLngBounds(opportunities.map(o => [o.lat, o.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [opportunities, map]);
  return null;
}

export default function FinancingPage() {
  const [selectedType, setSelectedType] = useState("all");
  const [selectedOpp, setSelectedOpp] = useState(null);

  const filteredOpportunities = selectedType === "all" 
    ? FINANCING_OPPORTUNITIES 
    : FINANCING_OPPORTUNITIES.filter(o => o.type === selectedType);

  const totalValue = FINANCING_OPPORTUNITIES.reduce((sum, o) => sum + o.amount, 0);

  return (
    <div data-testid="financing-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Financing
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Commodity financing opportunities and trade finance
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-32 h-8 rounded-none border-zinc-800 bg-black text-xs font-mono">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 rounded-none">
              <SelectItem value="all" className="text-xs font-mono">All Types</SelectItem>
              <SelectItem value="Harvest" className="text-xs font-mono">Harvest</SelectItem>
              <SelectItem value="Export" className="text-xs font-mono">Export</SelectItem>
              <SelectItem value="Trade" className="text-xs font-mono">Trade</SelectItem>
              <SelectItem value="Import" className="text-xs font-mono">Import</SelectItem>
              <SelectItem value="Supply" className="text-xs font-mono">Supply</SelectItem>
              <SelectItem value="Mining" className="text-xs font-mono">Mining</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Total Pipeline</p>
            <p className="text-2xl font-mono font-bold text-[#00ff41]">${totalValue.toFixed(1)}M</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Active Deals</p>
            <p className="text-2xl font-mono font-bold text-zinc-100">{FINANCING_OPPORTUNITIES.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Avg Deal Size</p>
            <p className="text-2xl font-mono font-bold text-[#0ea5e9]">${(totalValue / FINANCING_OPPORTUNITIES.length).toFixed(1)}M</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardContent className="p-3">
            <p className="text-[10px] text-zinc-500 uppercase font-mono">Avg Return</p>
            <p className="text-2xl font-mono font-bold text-[#eab308]">
              +{(FINANCING_OPPORTUNITIES.reduce((sum, o) => sum + o.change, 0) / FINANCING_OPPORTUNITIES.length).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <Card className="lg:col-span-2 bg-zinc-950/50 border-zinc-800 rounded-none overflow-hidden">
          <CardHeader className="pb-2 px-4 pt-4">
            <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[#00ff41]" />
              Financing Opportunities
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 h-[400px]">
            <MapContainer
              center={[10, 20]}
              zoom={2}
              style={{ height: "100%", width: "100%", background: "#0a0a0c" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <FitBounds opportunities={filteredOpportunities} />
              {filteredOpportunities.map((opp) => (
                <CircleMarker
                  key={opp.id}
                  center={[opp.lat, opp.lng]}
                  radius={Math.sqrt(opp.amount) * 2}
                  fillColor="#00ff41"
                  fillOpacity={0.6}
                  stroke={true}
                  color="#00ff41"
                  weight={1}
                  eventHandlers={{
                    click: () => setSelectedOpp(opp),
                  }}
                >
                  <Popup>
                    <div className="bg-zinc-900 p-3 min-w-[180px] border border-zinc-700">
                      <p className="font-mono font-bold text-zinc-100 text-sm">{opp.name}</p>
                      <p className="text-[10px] text-zinc-500 font-mono">{opp.region}</p>
                      <div className="mt-2 space-y-1">
                        <div className="flex justify-between text-[10px] font-mono">
                          <span className="text-zinc-500">Amount:</span>
                          <span className="text-[#00ff41]">${opp.amount}M</span>
                        </div>
                        <div className="flex justify-between text-[10px] font-mono">
                          <span className="text-zinc-500">Return:</span>
                          <span className="text-[#00ff41]">+{opp.change}%</span>
                        </div>
                        <div className="flex justify-between text-[10px] font-mono">
                          <span className="text-zinc-500">Commodity:</span>
                          <span className="text-zinc-300">{opp.commodity}</span>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </CardContent>
        </Card>

        {/* Side Panels */}
        <div className="space-y-4">
          {/* Top Gainers */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#00ff41]" />
                Top Opportunities
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {[...FINANCING_OPPORTUNITIES]
                .sort((a, b) => b.change - a.change)
                .slice(0, 4)
                .map((opp) => (
                  <div 
                    key={opp.id} 
                    className="flex items-center justify-between py-2 border-b border-zinc-900 cursor-pointer hover:bg-zinc-900/50"
                    onClick={() => setSelectedOpp(opp)}
                  >
                    <div>
                      <p className="text-xs text-zinc-300 font-mono">{opp.name}</p>
                      <p className="text-[9px] text-zinc-600 font-mono">{opp.region}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-mono text-zinc-300">${opp.amount}M</p>
                      <p className="text-[10px] font-mono text-[#00ff41]">+{opp.change}%</p>
                    </div>
                  </div>
                ))}
            </CardContent>
          </Card>

          {/* Recently Financed */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-[#0ea5e9]" />
                Recently Financed
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {RECENTLY_FINANCED.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-900">
                  <div>
                    <p className="text-xs text-zinc-300 font-mono">{item.name}</p>
                  </div>
                  <div className="text-right flex items-center gap-2">
                    <p className="text-xs font-mono text-zinc-400">${item.amount}M</p>
                    <Badge 
                      variant="outline" 
                      className={`rounded-none text-[8px] ${
                        item.status === "Completed" ? "border-[#00ff41] text-[#00ff41]" :
                        item.status === "In Progress" ? "border-[#0ea5e9] text-[#0ea5e9]" :
                        "border-[#eab308] text-[#eab308]"
                      }`}
                    >
                      {item.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Leaflet Popup Styles */}
      <style>{`
        .leaflet-popup-content-wrapper {
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
          border-radius: 0 !important;
        }
        .leaflet-popup-content {
          margin: 0 !important;
        }
        .leaflet-popup-tip-container {
          display: none;
        }
      `}</style>
    </div>
  );
}
